import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import './CarBooking.css';
import './App.css';

// Helper function to format currency in Indian numbering system
const formatIndianCurrency = (amount) => {
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Return '0' if invalid or zero
  if (!numAmount || isNaN(numAmount)) return '0';
  
  // Format using Indian locale
  return numAmount.toLocaleString('en-IN');
};

// Header component (defined outside to prevent re-creation on every render)
const Header = ({ children }) => (
  <header className="App-header">
    {children}
  </header>
);

function CarBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const { carLink } = location.state || {};

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const currentCarLink = useRef(carLink);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    weddingDate: '',
    weddingCity: '',
    weddingState: '',
    weddingZipCode: '',
    carColour: '',
    carDecoration: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bookingRequestId, setBookingRequestId] = useState(null);
  const [lookChoice, setLookChoice] = useState('old'); // 'old' or 'new'
  const [bookingChoice, setBookingChoice] = useState('4hr'); // '4hr' or '8hr'
  
  // Fetch car details from API
  useEffect(() => {
    // Reset fetch flag if carLink changes
    if (currentCarLink.current !== carLink) {
      hasFetched.current = false;
      currentCarLink.current = carLink;
    }

    // Prevent duplicate fetch in strict mode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchCarDetails = async () => {
      if (!carLink) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`https://api.vidaai.com/api/v1/car/${carLink}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.data) {
          const carData = data.data;
          
          // Parse reservation data (they come as JSON strings)
          const parseReservation = (resStr) => {
            if (!resStr) return null;
            try {
              return typeof resStr === 'string' ? JSON.parse(resStr) : resStr;
            } catch (e) {
              console.error('Error parsing reservation data:', e);
              return null;
            }
          };
          
          // Parse colour options from API (comma-separated)
          let colourOptions = ['Any available colour']; // Default
          if (carData.colour && carData.colour.trim()) {
            const colours = carData.colour.split(',').map(c => c.trim()).filter(Boolean);
            if (colours.length > 0) {
              colourOptions = colours;
            }
          }

          // Parse decoration options from API (comma-separated)
          let decorationOptions = ['I will select later']; // Default
          if (carData.decoration && carData.decoration.trim()) {
            const decorations = carData.decoration.split(',').map(d => d.trim()).filter(Boolean);
            if (decorations.length > 0) {
              decorationOptions = decorations;
            }
          }

          const carInfo = {
            make: carData.make,
            model: carData.model,
            category: carData.category || 'Luxury Car',
            price4hr: Number(carData['4-hr']) || 0,
            price8hr: Number(carData['8-hr']) || 0,
            price4hrOld: Number(carData['4-hr-old']) || 0,
            price8hrOld: Number(carData['8-hr-old']) || 0,
            deposit: Number(carData['deposit']) || 0,
            depositOld: Number(carData['deposit-old']) || 0,
            decorationPrice: Number(carData['decoration-price']) || 0,
            gstPercent: Number(carData['gst']) || 12, // Default to 12% if not specified
            roadLegal: carData['road-legal'] || 'Yes', // Default to 'Yes' if not specified
            colourOptions: colourOptions,
            decorationOptions: decorationOptions,
            hr4Reservation: parseReservation(carData['hr4-reservation']),
            hr8Reservation: parseReservation(carData['hr8-reservation']),
            hr4ReservationOld: parseReservation(carData['hr4-reservation-old']),
            hr8ReservationOld: parseReservation(carData['hr8-reservation-old'])
          };

          setCar(carInfo);
          
          // Set default lookChoice to 'new' if 8-hr-old pricing is not available
          const hasOldPricing = (carData['8-hr-old'] > 0);
          if (!hasOldPricing) {
            setLookChoice('new');
          }

          // Track add to cart events when car booking page is loaded
          // Default to 8hr pricing if available, otherwise 4hr
          const defaultPrice = carInfo.price8hr > 0 ? carInfo.price8hr : carInfo.price4hr;
          const defaultValue = defaultPrice + carInfo.decorationPrice;
          
          // Google Analytics 4 - add_to_cart event
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'add_to_cart', {
              currency: 'INR',
              value: defaultValue,
              items: [{
                item_id: carLink,
                item_name: `${carInfo.make} ${carInfo.model}`,
                item_category: carInfo.category,
                price: defaultPrice,
                quantity: 1
              }]
            });
          }
          
          // Facebook Pixel - AddToCart event
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'AddToCart', {
              value: defaultValue,
              currency: 'INR',
              content_name: `${carInfo.make} ${carInfo.model}`,
              content_category: carInfo.category,
              content_ids: [carLink],
              content_type: 'product'
            });
          }
        } else {
          throw new Error('Car not found');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching car details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [carLink]);
  
  // Auto-select booking choice if only one option is available
  useEffect(() => {
    if (!car) return;
    
    const price4hr = lookChoice === 'old' ? car.price4hrOld : car.price4hr;
    const price8hr = lookChoice === 'old' ? car.price8hrOld : car.price8hr;
    
    // If only 4hr is available
    if (price4hr > 0 && price8hr === 0) {
      setBookingChoice('4hr');
    }
    // If only 8hr is available
    else if (price4hr === 0 && price8hr > 0) {
      setBookingChoice('8hr');
    }
    // If current selection has zero price, switch to available option
    else if (bookingChoice === '4hr' && price4hr === 0 && price8hr > 0) {
      setBookingChoice('8hr');
    }
    else if (bookingChoice === '8hr' && price8hr === 0 && price4hr > 0) {
      setBookingChoice('4hr');
    }
  }, [car, lookChoice]);
  
  // Calculate pricing data from car details based on look choice
  const pricingData = car ? {
    '4hr': {
      basePrice: lookChoice === 'old' ? car.price4hrOld : car.price4hr
    },
    '8hr': {
      basePrice: lookChoice === 'old' ? car.price8hrOld : car.price8hr
    }
  } : {
    '4hr': {
      basePrice: 0
    },
    '8hr': {
      basePrice: 0
    }
  };
  
  // Calculate payment breakdown based on look and booking choices using formulas
  const calculatePayment = () => {
    if (!car) {
      return {
        payToReserve: 0,
        decorationCharge: 0,
        paySevenDaysPrior: 0,
        total: 0,
        gst: 0,
        grandTotal: 0
      };
    }

    // Get the selected rental duration charge and ensure it's a number
    const rentalCharge = Number(pricingData[bookingChoice].basePrice) || 0;
    
    // Get decoration charge and ensure it's a number
    const decorationCharge = Number(car.decorationPrice) || 0;
    
    // Calculate Pay to Reserve based on look choice and ensure it's a number
    const payToReserve = Number(lookChoice === 'old' ? (car.depositOld || 0) : (car.deposit || 0)) || 0;
    
    // Calculate Total = Rental Charge + Decoration Charge
    const total = rentalCharge + decorationCharge;
    
    // Calculate GST = Total * GST%
    const gstPercent = Number(car.gstPercent) || 12;
    const gst = Math.round(total * (gstPercent / 100));
    
    // Calculate Grand Total = Total + GST
    const grandTotal = total + gst;
    
    // Calculate Pay 7 Days Prior = Grand Total - Pay to Reserve
    const paySevenDaysPrior = grandTotal - payToReserve;
    
    return {
      payToReserve: payToReserve,
      decorationCharge: decorationCharge,
      paySevenDaysPrior: paySevenDaysPrior,
      total: total,
      gst: gst,
      grandTotal: grandTotal
    };
  };
  
  const payment = calculatePayment();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.weddingDate) {
      newErrors.weddingDate = 'Wedding date is required';
    }

    if (!formData.weddingCity.trim()) {
      newErrors.weddingCity = 'Wedding city is required';
    }

    if (!formData.weddingState.trim()) {
      newErrors.weddingState = 'Wedding state is required';
    }

    if (!formData.weddingZipCode.trim()) {
      newErrors.weddingZipCode = 'Zip code is required';
    } else if (!/^\d{6}$/.test(formData.weddingZipCode)) {
      newErrors.weddingZipCode = 'Please enter a valid 6-digit zip code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        // Prepare the booking data in the required format
        const bookingData = {
          timestamp: new Date().toISOString(),
          carMake: car.make,
          carModel: car.model,
          carCategory: car.category,
          carLink: carLink,
          lookChoice: lookChoice,
          rentalDuration: bookingChoice,
          rentalLabel: car.roadLegal === 'Yes' 
            ? (bookingChoice === '4hr' ? '4 Hours / 40 KMs' : '8 Hours / 80 KMs')
            : (bookingChoice === '4hr' ? '4 Hours' : '8 Hours'),
          basePrice: pricingData[bookingChoice].basePrice,
          payToReserve: payment.payToReserve,
          paySevenDaysPrior: payment.paySevenDaysPrior,
          total: payment.total,
          gst: payment.gst,
          grandTotal: payment.grandTotal,
          customerName: formData.name,
          customerPhone: formData.phone,
          customerEmail: formData.email,
          weddingDate: formData.weddingDate,
          weddingCity: formData.weddingCity,
          weddingState: formData.weddingState,
          weddingZipCode: formData.weddingZipCode,
          carColour: formData.carColour || 'Not specified',
          carDecoration: formData.carDecoration || 'Not specified'
        };

        console.log('Submitting booking data:', bookingData);

        // Send data to the backend API endpoint
        const response = await fetch('https://api.vidaai.com/api/v1/booking-request', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Booking submitted successfully:', result);
        
        // Check if API response has success and bookingRequestId
        if (result.success && result.bookingRequestId) {
          setBookingRequestId(result.bookingRequestId);
        }
        
        // Track Google Analytics 4 Purchase event
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'purchase', {
            transaction_id: result.bookingRequestId || ('VI' + Date.now().toString().slice(-8) + Math.floor(Math.random() * 100).toString().padStart(2, '0')),
            value: payment.total,
            currency: 'INR',
            items: [{
              item_id: carLink,
              item_name: `${car.make} ${car.model}`,
              item_category: car.category,
              price: pricingData[bookingChoice].basePrice,
              quantity: 1
            }],
            // User data for enhanced conversions
            user_data: {
              email_address: formData.email,
              phone_number: formData.phone,
              address: {
                first_name: formData.name.split(' ')[0],
                last_name: formData.name.split(' ').slice(1).join(' ') || formData.name.split(' ')[0],
                city: formData.weddingCity,
                region: formData.weddingState,
                postal_code: formData.weddingZipCode
              }
            }
          });
        }
        
        // Track Facebook Pixel Purchase event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            value: payment.total,
            currency: 'INR',
            content_name: `${car.make} ${car.model}`,
            content_category: car.category,
            content_ids: [carLink],
            content_type: 'product'
          }, {
            // User data for advanced matching
            em: formData.email,
            ph: formData.phone,
            fn: formData.name.split(' ')[0], // First name
            ln: formData.name.split(' ').slice(1).join(' ') || formData.name.split(' ')[0], // Last name or full name
            ct: formData.weddingCity,
            st: formData.weddingState,
            zp: formData.weddingZipCode
          });
        }
        
        setIsSubmitted(true);
      } catch (error) {
        console.error('Error submitting booking:', error);
        // Show error to user
        alert('There was an error submitting your booking. Please try again or contact us directly via WhatsApp at +91-9619905105');
      }
    }
  };

  // Handle cancel/back navigation
  const handleCancel = () => {
    navigate(-1);
  };

  // If loading, show loading state
  if (loading) {
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="booking-container">
            <div className="loading">Loading car details...</div>
          </div>
        </Header>
        <Footer />
      </div>
    );
  }

  // If error fetching car details
  if (error) {
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="booking-container">
            <div className="error">
              <p>Error loading car details: {error}</p>
              <Link to="/" className="back-link">Go to Home</Link>
            </div>
          </div>
        </Header>
        <Footer />
      </div>
    );
  }

  // If no car link was passed, show error
  if (!carLink || !car) {
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="booking-container">
            <div className="error">
              <p>No car selected. Please select a car first.</p>
              <Link to="/" className="back-link">Go to Home</Link>
            </div>
          </div>
        </Header>
        <Footer />
      </div>
    );
  }

  // Show success message after submission
  if (isSubmitted) {
    // Use booking request ID from API response, or generate fallback
    const bookingRef = bookingRequestId || ('VI' + Date.now().toString().slice(-8) + Math.floor(Math.random() * 100).toString().padStart(2, '0'));
    
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="booking-container">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Thank you for your booking!</h2>
              <p>Your booking request <strong>#{bookingRef}</strong> is registered.</p>
              <p className="booking-note">Your booking is not yet confirmed. We will reach out to you within 24 hours to confirm.</p>
              <div className="booking-details">
                <p>Vehicle: <strong>{car.make} {car.model}</strong></p>
                <p>Contact: <strong>{formData.phone}</strong></p>
                <p>Email: <strong>{formData.email}</strong></p>
              </div>
              <Link to="/" className="btn btn-home">Go to Home</Link>
            </div>
          </div>
        </Header>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation />
      <Header>
        <div className="booking-container">
          <div className="booking-header">
            <h1 className="booking-title">Book Your Wedding Car</h1>
            <p className="booking-subtitle">Complete the form below to request a booking</p>
          </div>

          <div className="selected-car-info">
            <h3>{car.make} {car.model}</h3>
            <p>{car.category}</p>
          </div>

          <form onSubmit={handleSubmit} className="booking-form">
            <div className="form-section">
              <h3 className="section-title">Personal Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-row two-columns">
                <div className="form-group">
                  <label htmlFor="phone">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="10-digit phone number"
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Wedding Details</h3>
              
              <div className="form-group">
                <label htmlFor="weddingDate">
                  Wedding Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="weddingDate"
                  name="weddingDate"
                  value={formData.weddingDate}
                  onChange={handleChange}
                  className={errors.weddingDate ? 'error' : ''}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.weddingDate && <span className="error-message">{errors.weddingDate}</span>}
              </div>

              <div className="form-row three-columns">
                <div className="form-group">
                  <label htmlFor="weddingState">
                    State <span className="required">*</span>
                  </label>
                  <select
                    id="weddingState"
                    name="weddingState"
                    value={formData.weddingState}
                    onChange={handleChange}
                    className={errors.weddingState ? 'error' : ''}
                  >
                    <option value="">Select State</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                    <option value="Chandigarh">Chandigarh</option>
                    <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                    <option value="Ladakh">Ladakh</option>
                    <option value="Lakshadweep">Lakshadweep</option>
                    <option value="Puducherry">Puducherry</option>
                  </select>
                  {errors.weddingState && <span className="error-message">{errors.weddingState}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="weddingCity">
                    City <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="weddingCity"
                    name="weddingCity"
                    value={formData.weddingCity}
                    onChange={handleChange}
                    className={errors.weddingCity ? 'error' : ''}
                    placeholder="City"
                  />
                  {errors.weddingCity && <span className="error-message">{errors.weddingCity}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="weddingZipCode">
                    Zip Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="weddingZipCode"
                    name="weddingZipCode"
                    value={formData.weddingZipCode}
                    onChange={handleChange}
                    className={errors.weddingZipCode ? 'error' : ''}
                    placeholder="6-digit zip code"
                    maxLength="6"
                  />
                  {errors.weddingZipCode && <span className="error-message">{errors.weddingZipCode}</span>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Car Preferences</h3>
              
              <div className="form-row two-columns">
                <div className="form-group">
                  <label htmlFor="carColour">
                    Car Colour
                  </label>
                  <select
                    id="carColour"
                    name="carColour"
                    value={formData.carColour}
                    onChange={handleChange}
                    className={errors.carColour ? 'error' : ''}
                  >
                    <option value="">Select Colour</option>
                    {car.colourOptions.map((colour, index) => (
                      <option key={index} value={colour}>
                        {colour}
                      </option>
                    ))}
                  </select>
                  {errors.carColour && <span className="error-message">{errors.carColour}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="carDecoration">
                    Car Decoration
                  </label>
                  <select
                    id="carDecoration"
                    name="carDecoration"
                    value={formData.carDecoration}
                    onChange={handleChange}
                    className={errors.carDecoration ? 'error' : ''}
                  >
                    <option value="">Select Decoration</option>
                    {car.decorationOptions.map((decoration, index) => (
                      <option key={index} value={decoration}>
                        {decoration}
                      </option>
                    ))}
                  </select>
                  {errors.carDecoration && <span className="error-message">{errors.carDecoration}</span>}
                </div>
              </div>
            </div>

            <div className="payment-details-section">
              <h3 className="section-title">Choose Rental Option</h3>
              
              {car.price8hrOld > 0 && (
                <div className="look-choice-section">
                  <h4>Select Car Age</h4>
                  <div className="booking-choice-toggle">
                    <button
                      type="button"
                      className={`choice-button ${lookChoice === 'old' ? 'active' : ''}`}
                      onClick={() => setLookChoice('old')}
                    >
                      <div className="choice-title">Old Look</div>
                      <div className="choice-subtitle">Model year 2019 or before</div>
                    </button>
                    <button
                      type="button"
                      className={`choice-button ${lookChoice === 'new' ? 'active' : ''}`}
                      onClick={() => setLookChoice('new')}
                    >
                      <div className="choice-title">New Look</div>
                      <div className="choice-subtitle">Model year 2020 or later</div>
                    </button>
                  </div>
                </div>
              )}

              <div className="duration-choice-section">
                <h4>Select Rental Duration</h4>
                <div className="booking-choice-toggle">
                  {pricingData['4hr'].basePrice > 0 && (
                    <button
                      type="button"
                      className={`choice-button ${bookingChoice === '4hr' ? 'active' : ''}`}
                      onClick={() => setBookingChoice('4hr')}
                    >
                      <div className="choice-title">{car.roadLegal === 'Yes' ? '4 Hours / 40 KMs' : '4 Hours'}</div>
                      <div className="choice-price">₹{formatIndianCurrency(pricingData['4hr'].basePrice)}</div>
                    </button>
                  )}
                  {pricingData['8hr'].basePrice > 0 && (
                    <button
                      type="button"
                      className={`choice-button ${bookingChoice === '8hr' ? 'active' : ''}`}
                      onClick={() => setBookingChoice('8hr')}
                    >
                      <div className="choice-title">{car.roadLegal === 'Yes' ? '8 Hours / 80 KMs' : '8 Hours'}</div>
                      <div className="choice-price">₹{formatIndianCurrency(pricingData['8hr'].basePrice)}</div>
                    </button>
                  )}
                </div>
              </div>

              <div className="payment-schedule">
                <h4>Payment Schedule</h4>
                <div className="payment-breakdown">
                  <div className="payment-row">
                    <span className="payment-label">Car Rental with Chauffeur</span>
                    <span className="payment-value">₹{formatIndianCurrency(pricingData[bookingChoice].basePrice)}</span>
                  </div>
                  {payment.decorationCharge > 0 && (
                    <div className="payment-row">
                      <span className="payment-label">Decoration</span>
                      <span className="payment-value">₹{formatIndianCurrency(payment.decorationCharge)}</span>
                    </div>
                  )}
                  <div className="payment-row subtotal">
                    <span className="payment-label">Total</span>
                    <span className="payment-value">₹{formatIndianCurrency(payment.total)}</span>
                  </div>
                  <div className="payment-row">
                    <span className="payment-label">GST ({car?.gstPercent || 12}%)</span>
                    <span className="payment-value">₹{formatIndianCurrency(payment.gst)}</span>
                  </div>
                  <div className="payment-row grand-total">
                    <span className="payment-label">Grand Total</span>
                    <span className="payment-value">₹{formatIndianCurrency(payment.grandTotal)}</span>
                  </div>
                  <div className="payment-row">
                    <span className="payment-label">Pay to Reserve</span>
                    <span className="payment-value">₹{formatIndianCurrency(payment.payToReserve)}</span>
                  </div>
                  <div className="payment-row">
                    <span className="payment-label">Pay 7 Days Prior</span>
                    <span className="payment-value">₹{formatIndianCurrency(payment.paySevenDaysPrior)}</span>
                  </div>
                </div>
                <p className="payment-note">* Advance payment required to confirm booking</p>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Booking Request
                </button>
              </div>
            </div>
          </form>
        </div>
      </Header>
      <Footer />
    </div>
  );
}

export default CarBooking;
